import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getRepositoryPipelines, bulkRunPipelines } from "../api";
import { RefreshCw, Play, Database, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  useWorkspaceQuery,
  useRepositoriesQuery,
  useBranchesQuery,
  useRepositoryCache,
} from "../hooks/useRepositoryApi";

// Function to check if a pipeline is a deployment pipeline
const isDeploymentPipeline = (pipeline) => {
  if (!pipeline || !pipeline.selector || !pipeline.selector.pattern) {
    return false;
  }

  // Check if the pattern suggests it's a deployment pipeline
  // Using the "Deploy to" pattern as an identifier
  const pattern = pipeline.selector.pattern;
  return pattern.toLowerCase().includes("deploy to");
};

// Update SelectComponent to fix hooks error
const SelectComponent = ({
  label,
  value,
  onChange,
  disabled,
  children,
  options,
  showAutosuggest = false,
}) => {
  // Always declare hooks at the top level, regardless of which rendering path we take
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (!options || !inputValue) return options;
    return options.filter((option) =>
      option.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [options, inputValue]);

  // If not using autosuggest, render normal select
  if (!showAutosuggest) {
    return (
      <div className="mb-4">
        <Label htmlFor={label} className="mb-2 block">
          {label}
        </Label>
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id={label} className="w-full">
            <SelectValue placeholder={`Select ${label}`} />
          </SelectTrigger>
          <SelectContent>{children}</SelectContent>
        </Select>
      </div>
    );
  }

  // Autosuggest implementation
  return (
    <div className="mb-4">
      <Label htmlFor={label} className="mb-2 block">
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            {value ? value : `Select ${label}`}
            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput
              placeholder={`Search ${label}...`}
              value={inputValue}
              onValueChange={setInputValue}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
              <CommandGroup>
                {filteredOptions &&
                  filteredOptions.map((option) => (
                    <CommandItem
                      key={option}
                      value={option}
                      onSelect={() => {
                        onChange(option);
                        setOpen(false);
                        setInputValue("");
                      }}
                    >
                      {option}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const PipelinesPage = () => {
  const [pipelines, setPipelines] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [selectedRepositories, setSelectedRepositories] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [isRunningPipelines, setIsRunningPipelines] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(
    process.env.REACT_APP_DEFAULT_REPO_SEARCH || ""
  );
  const { toast } = useToast();
  const [groupByProject, setGroupByProject] = useState(true);
  const [projectsExpanded, setProjectsExpanded] = useState({});

  // Use React Query hooks
  const repositoryCache = useRepositoryCache();

  // Fetch workspaces with React Query
  const {
    data: workspaces = [],
    isLoading: isLoadingWorkspaces,
    error: workspacesError,
  } = useWorkspaceQuery();

  // Fetch repositories for selected workspace
  const {
    data: repositories = [],
    isLoading: isLoadingRepos,
    error: repositoriesError,
  } = useRepositoriesQuery(selectedWorkspace);

  // Fetch branches for selected repository
  const {
    data: branches = [],
    isLoading: isLoadingBranches,
    error: branchesError,
    refetch: refetchBranches,
  } = useBranchesQuery(
    selectedWorkspace,
    selectedRepositories.length > 0 ? selectedRepositories[0] : null,
    {
      // Only enable this query when we have a repository selected
      enabled: !!(selectedWorkspace && selectedRepositories.length > 0),
    }
  );

  // Get default branch from localStorage
  const DEFAULT_BRANCH = localStorage.getItem("defaultBranch") || "";

  // Set default workspace on initial load
  useEffect(() => {
    if (workspaces.length > 0 && !selectedWorkspace) {
      // Check for default workspace from env
      const defaultWorkspace = process.env.REACT_APP_DEFAULT_WORKSPACE || "";

      // Check if the default workspace exists in the list
      const workspaceExists =
        defaultWorkspace &&
        workspaces.some((ws) => ws.slug === defaultWorkspace);

      // Use default or first workspace
      const workspaceToUse = workspaceExists
        ? defaultWorkspace
        : workspaces[0].slug;

      if (workspaceToUse) {
        console.log(`Setting default workspace: ${workspaceToUse}`);
        setSelectedWorkspace(workspaceToUse);
      }
    }
  }, [workspaces, selectedWorkspace]);

  // Set default branch when branches are loaded if we have a default branch set
  useEffect(() => {
    if (branches.length > 0 && DEFAULT_BRANCH && !selectedBranch) {
      // Check if the default branch exists in the list
      const defaultExists = branches.some(
        (branch) => branch.name === DEFAULT_BRANCH
      );

      if (defaultExists) {
        console.log(`Setting default branch: ${DEFAULT_BRANCH}`);
        setSelectedBranch(DEFAULT_BRANCH);

        // Refresh pipelines after setting the branch
        if (selectedRepositories.length > 0) {
          fetchPipelines(selectedWorkspace, selectedRepositories[0]);
        }
      }
    }
    // fetchPipelines intentionally reads the latest selected branch state while
    // this effect only reacts to branch data/default selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, DEFAULT_BRANCH, selectedBranch, selectedRepositories, selectedWorkspace]);

  // Function to fetch pipelines
  const fetchPipelines = async (workspace, repoSlug) => {
    try {
      console.log(`Fetching pipelines for ${workspace}/${repoSlug}`);
      setIsLoadingPipelines(true);
      setPipelines([]);
      setSelectedPipeline("");

      const pipelineData = await getRepositoryPipelines(workspace, repoSlug);
      console.log("Pipeline data received:", pipelineData);

      if (pipelineData) {
        // Ensure we have a definitions array even if empty
        if (!pipelineData.definitions) {
          pipelineData.definitions = [];
        }

        // If we have a selected branch, make sure we have a pipeline for it
        // This ensures we have the current branch pipeline even if it's not in the API response
        if (selectedBranch) {
          // Check if we already have a pipeline for this branch
          const branchPipelineExists = pipelineData.definitions.some(
            (def) =>
              def.selector &&
              def.selector.type === "branches" &&
              def.selector.pattern === selectedBranch
          );

          // If we don't have a pipeline for this branch, add it
          if (!branchPipelineExists) {
            pipelineData.definitions.push({
              selector: {
                type: "branches",
                pattern: selectedBranch,
              },
              displayName: `Branch: ${selectedBranch}`,
            });
          }
        }

        // Set the pipelines data
        setPipelines(pipelineData);

        // Find the deployment pipelines
        const deploymentPipelines =
          pipelineData.definitions.filter(isDeploymentPipeline);

        // If we have deployment pipelines, select the first one by default
        if (deploymentPipelines.length > 0) {
          const firstDeploymentPipeline = deploymentPipelines[0];
          const pipelineValue = JSON.stringify({
            type: firstDeploymentPipeline.selector.type,
            pattern: firstDeploymentPipeline.selector.pattern,
          });
          console.log(
            `Auto-selecting deployment pipeline: ${
              firstDeploymentPipeline.displayName ||
              firstDeploymentPipeline.selector.pattern
            }`
          );
          setSelectedPipeline(pipelineValue);
        }
        // Otherwise, check if we should select a branch-specific pipeline
        else if (selectedBranch) {
          const branchPipeline = pipelineData.definitions.find(
            (p) =>
              p.selector &&
              p.selector.type === "branches" &&
              p.selector.pattern === selectedBranch
          );

          if (branchPipeline) {
            const pipelineValue = JSON.stringify({
              type: branchPipeline.selector.type,
              pattern: branchPipeline.selector.pattern,
            });
            console.log(
              `Pre-selecting branch pipeline for ${selectedBranch}:`,
              pipelineValue
            );
            setSelectedPipeline(pipelineValue);
          }
        }

        // Show a success message if we found custom pipelines
        if (deploymentPipelines.length > 0) {
          toast({
            title: "Pipelines loaded",
            description: `Found ${deploymentPipelines.length} deployment pipelines`,
          });
        }
      } else {
        console.log("No pipelines found or invalid pipeline data");
        setPipelines({ definitions: [] });
        setSelectedPipeline("");
      }

      setIsLoadingPipelines(false);
      return pipelineData;
    } catch (err) {
      console.error("Error fetching pipelines:", err);
      setPipelines({ definitions: [] });
      setSelectedPipeline("");
      setIsLoadingPipelines(false);
      toast({
        title: "Error",
        description: "Failed to load pipelines for the selected repository",
        variant: "destructive",
      });
      return { definitions: [] };
    }
  };

  const handleWorkspaceChange = async (workspace) => {
    setSelectedWorkspace(workspace);
    setSelectedRepositories([]);
    setSelectedBranch("");
    setPipelines({ definitions: [] });
    setSelectedPipeline("");
  };

  const handleRepositoryToggle = (repoSlug) => {
    setSelectedRepositories((prev) => {
      const isSelected = prev.includes(repoSlug);

      if (isSelected) {
        // Deselecting a repository
        const newSelection = prev.filter((slug) => slug !== repoSlug);

        // If we're deselecting all repositories, clear branches and pipelines
        if (newSelection.length === 0) {
          setSelectedBranch("");
          setPipelines({ definitions: [] });
          setSelectedPipeline("");
        }

        return newSelection;
      } else {
        // Selecting a repository
        const newSelection = [...prev, repoSlug];

        // If this is the first selection, branches will be loaded automatically by React Query
        return newSelection;
      }
    });
  };

  const handleBranchChange = (branch) => {
    console.log(`Selected branch changed to: ${branch}`);
    setSelectedBranch(branch);

    // Refresh pipelines when branch changes to show branch-specific pipelines
    if (selectedRepositories.length > 0) {
      fetchPipelines(selectedWorkspace, selectedRepositories[0]);
    }
  };

  const handlePipelineChange = (pipelineSelector) => {
    console.log(`Selected pipeline changed to: ${pipelineSelector}`);
    setSelectedPipeline(pipelineSelector);
  };

  // Add helper function to display the selected pipeline in the UI
  const getSelectedPipelineDisplayText = () => {
    if (!selectedPipeline) {
      return "No Pipeline Selected";
    }

    try {
      // Try to parse the JSON string
      const pipelineData = JSON.parse(selectedPipeline);

      // For deployment pipelines, return the exact pattern (which contains the deployment name)
      if (pipelineData.pattern.toLowerCase().includes("deploy to")) {
        return pipelineData.pattern;
      }

      // For branch pipelines
      if (pipelineData.type === "branches") {
        return `Branch: ${pipelineData.pattern}`;
      } else if (pipelineData.type === "custom") {
        return `Custom Pipeline: ${pipelineData.pattern}`;
      } else {
        return `${pipelineData.type}: ${pipelineData.pattern}`;
      }
    } catch (e) {
      // If parsing fails, just return the raw value
      return selectedPipeline;
    }
  };

  const handleRunPipelines = async () => {
    if (
      !selectedWorkspace ||
      selectedRepositories.length === 0 ||
      !selectedBranch ||
      !selectedPipeline
    ) {
      toast({
        title: "Missing information",
        description:
          "Please select workspace, repositories, branch, and pipeline",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsRunningPipelines(true);

      // Create pipeline configuration with the selected pipeline
      let pipelineConfig = {};

      if (selectedPipeline) {
        try {
          // Parse the stored pipeline data which contains both type and pattern
          const pipelineData = JSON.parse(selectedPipeline);

          console.log("Using pipeline configuration:", pipelineData);

          // For deployment pipelines (patterns containing "Deploy to"), always use type: "branches"
          if (pipelineData.pattern.toLowerCase().includes("deploy to")) {
            console.log("Using deployment pipeline:", pipelineData.pattern);
            // For deployment pipelines, ALWAYS use "branches" as the type
            pipelineConfig = {
              selector: {
                type: "branches", // MUST be "branches" to match Bitbucket payload format
                pattern: pipelineData.pattern,
              },
            };
          }
          // For standard branch pipelines, don't add a selector
          else if (
            pipelineData.type === "branches" &&
            pipelineData.pattern === selectedBranch
          ) {
            console.log("Using standard branch pipeline without selector");
            // Don't add a selector for the standard branch pipeline
          }
          // For other pipeline types
          else {
            pipelineConfig = {
              selector: {
                type: pipelineData.type,
                pattern: pipelineData.pattern,
              },
            };
          }
        } catch (err) {
          console.error("Error parsing pipeline data:", err);
          // Don't use any selector if we can't parse the pipeline data
          pipelineConfig = {};
        }
      }

      console.log("Running with pipeline config:", pipelineConfig);

      const results = await bulkRunPipelines(
        selectedWorkspace,
        selectedRepositories,
        selectedBranch,
        pipelineConfig
      );

      // Count successes and failures
      const successCount = results.filter((result) => result.success).length;
      const failCount = results.length - successCount;

      // Display more detailed error information
      const failedRepos = results
        .filter((result) => !result.success)
        .map((result) => ({
          repo: result.repository,
          error:
            result.error?.response?.data?.error?.detail ||
            result.error?.response?.data?.error?.message ||
            result.error?.message ||
            "Unknown error",
        }));

      if (failCount > 0) {
        console.error("Failed pipeline runs:", failedRepos);

        // Show a more detailed error toast
        toast({
          title: `Pipelines triggered (${successCount}/${results.length} successful)`,
          description: `Failed: ${failedRepos
            .map((f) => `${f.repo}: ${f.error}`)
            .join("; ")}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Pipelines triggered",
          description: `Successfully triggered ${successCount} pipelines`,
          variant: "default",
        });
      }

      setIsRunningPipelines(false);
    } catch (err) {
      console.error("Error running pipelines:", err);
      toast({
        title: "Error",
        description:
          err?.response?.data?.error?.detail ||
          err.message ||
          "Failed to run pipelines",
        variant: "destructive",
      });
      setIsRunningPipelines(false);
    }
  };

  const handleRefreshData = async () => {
    try {
      // Invalidate all cached data to force a refresh
      repositoryCache.invalidateAll();
    } catch (err) {
      setError("Failed to refresh data");
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive",
      });
    }
  };

  // Replace the useEffect for filtering repositories with useMemo
  const filteredProjectsSetting = localStorage.getItem("filteredProjects") || "[]";

  const filteredRepositories = useMemo(() => {
    // Get the filtered projects from localStorage
    const filteredProjects = JSON.parse(filteredProjectsSetting);

    // Filter repositories that aren't in the filtered projects list
    return repositories.filter((repo) => {
      // First apply the search term filter
      const matchesSearch =
        repo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        repo.slug.toLowerCase().includes(searchTerm.toLowerCase());

      // Then apply project filter if there are filtered projects
      const shouldInclude =
        !filteredProjects.length ||
        !repo.project ||
        !filteredProjects.includes(repo.project.key);

      return matchesSearch && shouldInclude;
    });
  }, [searchTerm, repositories, filteredProjectsSetting]);

  // Separate useEffect to initialize project expansion state only when groupByProject changes
  useEffect(() => {
    if (groupByProject) {
      const projects = {};
      // Get unique project keys from filtered repositories
      const projectKeys = [
        ...new Set(
          filteredRepositories
            .filter((repo) => repo.project)
            .map((repo) => repo.project.key)
        ),
      ];

      // Initialize all projects as expanded
      projectKeys.forEach((key) => {
        projects[key] = true;
      });

      setProjectsExpanded(projects);
    }
  }, [groupByProject, filteredRepositories]);

  // Helper function to toggle project expansion
  const toggleProject = (projectKey) => {
    setProjectsExpanded((prev) => ({
      ...prev,
      [projectKey]: !prev[projectKey],
    }));
  };

  // Helper function to group repositories by project
  const getGroupedRepositories = () => {
    if (!groupByProject) {
      return { null: filteredRepositories };
    }

    return filteredRepositories.reduce((groups, repo) => {
      const projectKey = repo.project ? repo.project.key : "No Project";
      if (!groups[projectKey]) {
        groups[projectKey] = [];
      }
      groups[projectKey].push(repo);
      return groups;
    }, {});
  };

  // Get projects with their repositories
  const groupedRepositories = getGroupedRepositories();

  // Helper function to get repositories for a specific project
  const getProjectRepositories = (projectKey) => {
    return filteredRepositories.filter((repo) =>
      repo.project
        ? repo.project.key === projectKey
        : projectKey === "No Project"
    );
  };

  // Helper function to check if all repositories in a project are selected
  const isProjectFullySelected = (projectKey) => {
    const projectRepos = getProjectRepositories(projectKey);
    return (
      projectRepos.length > 0 &&
      projectRepos.every((repo) => selectedRepositories.includes(repo.slug))
    );
  };

  // Helper function to check if any repositories in a project are selected
  const isProjectPartiallySelected = (projectKey) => {
    const projectRepos = getProjectRepositories(projectKey);
    return (
      projectRepos.some((repo) => selectedRepositories.includes(repo.slug)) &&
      !isProjectFullySelected(projectKey)
    );
  };

  // Helper function to toggle selection of all repositories in a project
  const toggleProjectSelection = (projectKey) => {
    const projectRepos = getProjectRepositories(projectKey);
    const projectSlugs = projectRepos.map((repo) => repo.slug);

    // Check if all repositories in this project are already selected
    const allSelected = isProjectFullySelected(projectKey);

    if (allSelected) {
      // If all already selected, deselect all in this project
      setSelectedRepositories((prev) =>
        prev.filter((slug) => !projectSlugs.includes(slug))
      );
    } else {
      // Otherwise, select all in this project
      setSelectedRepositories((prev) => {
        // Add all project repos that aren't already selected
        const newSelection = [...prev];
        projectSlugs.forEach((slug) => {
          if (!newSelection.includes(slug)) {
            newSelection.push(slug);
          }
        });
        return newSelection;
      });
    }
  };

  // Helper functions to expand/collapse all projects
  const expandAllProjects = () => {
    const expandedState = {};
    Object.keys(groupedRepositories).forEach((key) => {
      expandedState[key] = true;
    });
    setProjectsExpanded(expandedState);
  };

  const collapseAllProjects = () => {
    const collapsedState = {};
    Object.keys(groupedRepositories).forEach((key) => {
      collapsedState[key] = false;
    });
    setProjectsExpanded(collapsedState);
  };

  // Combine all potential errors
  const combinedError =
    error || workspacesError || repositoriesError || branchesError;
  const isLoading =
    isLoadingWorkspaces || (isLoadingRepos && !repositories.length);

  if (combinedError) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="text-destructive text-lg font-semibold">
          {combinedError.message || "Failed to load data"}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0">
      {!isLoadingRepos && repositories.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Configure and Run Pipelines</CardTitle>
                <CardDescription className="mt-1">
                  Select branch, pipeline, and run for{" "}
                  {selectedRepositories.length} selected repositories
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  selectedRepositories.length > 0
                    ? refetchBranches()
                    : toast({
                        title: "No repository selected",
                        description:
                          "Please select at least one repository first",
                        variant: "destructive",
                      })
                }
              >
                <RefreshCw className="mr-2 h-3 w-3" />
                Refresh Options
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                {isLoadingBranches ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                    Loading branches...
                  </div>
                ) : branches.length > 0 ? (
                  <SelectComponent
                    label="Branch"
                    value={selectedBranch}
                    onChange={handleBranchChange}
                    disabled={selectedRepositories.length === 0}
                    options={branches.map((branch) => branch.name)}
                    showAutosuggest={true}
                  />
                ) : (
                  <div className="text-muted-foreground mb-6">
                    {selectedRepositories.length === 0
                      ? "Select repositories first to load branches"
                      : "No branches found for selected repository. Try refreshing the data."}
                  </div>
                )}
              </div>

              <div>
                {isLoadingPipelines ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                    Loading pipelines...
                  </div>
                ) : (
                  <div>
                    <RadioGroup
                      value={selectedPipeline}
                      onValueChange={handlePipelineChange}
                      className="mt-2"
                    >
                      {/* Deployment Pipelines section */}
                      {pipelines &&
                        pipelines.definitions &&
                        pipelines.definitions.filter(isDeploymentPipeline)
                          .length > 0 && (
                          <div className="text-sm font-medium mb-2">
                            Deployment Pipelines:
                          </div>
                        )}

                      {/* Display deployment pipelines */}
                      {pipelines &&
                        pipelines.definitions &&
                        pipelines.definitions
                          .filter(isDeploymentPipeline)
                          .map((pipeline, index) => {
                            // Store with the EXACT selector format required by the Bitbucket API
                            // Always use type: "branches" for deployment pipelines as shown
                            // in the Bitbucket UI example
                            const pipelineValue = JSON.stringify({
                              type: "branches", // Force "branches" type for deployment pipelines
                              pattern: pipeline.selector.pattern,
                            });

                            return (
                              <div
                                key={`deployment-${index}`}
                                className="flex items-center space-x-2 mb-2"
                              >
                                <RadioGroupItem
                                  value={pipelineValue}
                                  id={`deployment-pipeline-${index}`}
                                />
                                <Label
                                  htmlFor={`deployment-pipeline-${index}`}
                                  className="cursor-pointer"
                                >
                                  {pipeline.displayName ||
                                    pipeline.selector.pattern}
                                </Label>
                              </div>
                            );
                          })}
                    </RadioGroup>
                    {pipelines &&
                      pipelines.definitions &&
                      pipelines.definitions.length === 0 && (
                        <div className="text-muted-foreground">
                          Using default pipeline (no custom pipelines found)
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>

            {selectedRepositories.length > 0 && (
              <div className="mt-6">
                <p className="text-muted-foreground mb-4">
                  Run pipelines for {selectedRepositories.length} selected
                  repositories
                  {selectedBranch ? (
                    <>
                      {" "}
                      on branch:{" "}
                      <span className="font-medium text-foreground">
                        {selectedBranch}
                      </span>
                    </>
                  ) : (
                    <> (select a branch first)</>
                  )}
                  {selectedPipeline ? (
                    <>
                      {" "}
                      using{" "}
                      <span className="font-medium text-foreground">
                        {getSelectedPipelineDisplayText()}
                      </span>
                    </>
                  ) : (
                    <> (select a pipeline first)</>
                  )}
                </p>

                <Button
                  className="w-full"
                  onClick={handleRunPipelines}
                  disabled={
                    isRunningPipelines ||
                    selectedRepositories.length === 0 ||
                    !selectedBranch ||
                    !selectedPipeline
                  }
                >
                  {isRunningPipelines ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-background border-t-transparent rounded-full animate-spin mr-2"></div>
                      Running Pipelines...
                    </div>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Run Pipelines ({selectedRepositories.length})
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isLoadingRepos ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : repositories.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle>Select Repositories</CardTitle>
              <div className="flex items-center space-x-4">
                <div className="w-40">
                  <Select
                    value={selectedWorkspace}
                    onValueChange={handleWorkspaceChange}
                    disabled={workspaces.length === 0}
                  >
                    <SelectTrigger id="workspace-select" className="w-full h-9">
                      <SelectValue placeholder="Select Workspace" />
                    </SelectTrigger>
                    <SelectContent>
                      {workspaces.map((workspace) => (
                        <SelectItem key={workspace.slug} value={workspace.slug}>
                          {workspace.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-64">
                  <Input
                    id="search"
                    placeholder="Search repositories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={!selectedWorkspace}
                    className="w-full h-9"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="group-by-project"
                    checked={groupByProject}
                    onCheckedChange={(checked) => setGroupByProject(!!checked)}
                  />
                  <Label htmlFor="group-by-project" className="cursor-pointer">
                    Group by Project
                  </Label>
                </div>
                <Button
                  onClick={handleRefreshData}
                  className="flex items-center"
                  variant="outline"
                  disabled={isLoadingRepos || isLoadingBranches}
                >
                  <RefreshCw
                    className={`mr-2 h-4 w-4 ${
                      isLoadingRepos || isLoadingBranches ? "animate-spin" : ""
                    }`}
                  />
                  Refresh Data
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="py-2">
            <div className="overflow-x-auto">
              <div className="flex justify-between items-center mb-2 px-3">
                <div className="flex items-center">
                  <Checkbox
                    id="select-all-repos"
                    checked={
                      selectedRepositories.length ===
                        filteredRepositories.length &&
                      filteredRepositories.length > 0
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        // Select all filtered repositories
                        setSelectedRepositories(
                          filteredRepositories.map((repo) => repo.slug)
                        );
                      } else {
                        // Deselect all
                        setSelectedRepositories([]);
                      }
                    }}
                  />
                  <Label
                    htmlFor="select-all-repos"
                    className="ml-2 cursor-pointer"
                  >
                    Select All
                  </Label>
                </div>
                <div className="text-sm text-muted-foreground">
                  {selectedRepositories.length} of {filteredRepositories.length}{" "}
                  selected
                </div>
              </div>

              {groupByProject ? (
                // Grouped repositories view
                <div>
                  <div className="flex justify-end mb-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={expandAllProjects}
                      className="text-xs mr-2 h-8"
                    >
                      Expand All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={collapseAllProjects}
                      className="text-xs h-8"
                    >
                      Collapse All
                    </Button>
                  </div>
                  {Object.entries(groupedRepositories).map(
                    ([projectKey, repos]) => {
                      // Skip rendering if there are no repositories in this project
                      if (repos.length === 0) return null;

                      // Get project details from the first repository in the group
                      const projectDetails = repos[0]?.project;
                      const isExpanded = projectsExpanded[projectKey] || false;

                      return (
                        <div
                          key={projectKey}
                          className="mb-2 border border-border rounded-md overflow-hidden"
                        >
                          <div
                            className="flex justify-between items-center p-2 bg-muted/70 cursor-pointer"
                            onClick={() => toggleProject(projectKey)}
                          >
                            <div className="flex items-center">
                              <div
                                className="w-12 px-3 flex justify-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleProjectSelection(projectKey);
                                }}
                              >
                                <Checkbox
                                  checked={isProjectFullySelected(projectKey)}
                                  className={
                                    isProjectPartiallySelected(projectKey)
                                      ? "opacity-70"
                                      : ""
                                  }
                                  onCheckedChange={() =>
                                    toggleProjectSelection(projectKey)
                                  }
                                />
                              </div>
                              <div className="flex items-center">
                                <span className="mr-2 text-xs text-muted-foreground w-4">
                                  {isExpanded ? "▼" : "►"}
                                </span>
                                <span className="font-medium">
                                  {projectDetails
                                    ? projectDetails.name
                                    : "No Project"}
                                </span>
                                {projectDetails && (
                                  <span className="text-xs ml-2 text-muted-foreground">
                                    ({projectKey})
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-muted-foreground pr-3">
                              {
                                selectedRepositories.filter((slug) =>
                                  repos.some((repo) => repo.slug === slug)
                                ).length
                              }{" "}
                              of {repos.length} selected
                            </div>
                          </div>

                          {isExpanded && (
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-muted/50 border-b border-border">
                                  <th className="px-3 py-2 text-left font-medium w-12">
                                    Select
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium w-full">
                                    Repository
                                  </th>
                                  <th className="px-3 py-2 text-center font-medium w-24">
                                    Type
                                  </th>
                                  <th className="px-3 py-2 text-center font-medium w-28">
                                    Variables
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {repos.map((repo) => (
                                  <tr
                                    key={repo.slug}
                                    className="border-b border-border hover:bg-muted/30 cursor-pointer"
                                    onClick={() =>
                                      handleRepositoryToggle(repo.slug)
                                    }
                                  >
                                    <td
                                      className="px-3 py-2 w-12"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex justify-center">
                                        <Checkbox
                                          checked={selectedRepositories.includes(
                                            repo.slug
                                          )}
                                          onCheckedChange={() =>
                                            handleRepositoryToggle(repo.slug)
                                          }
                                          id={`repo-${repo.slug}`}
                                        />
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 w-full">
                                      <div className="font-medium text-left">
                                        {repo.name}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 w-24 text-center">
                                      <div className="inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs">
                                        {repo.is_private ? "Private" : "Public"}
                                      </div>
                                    </td>
                                    <td
                                      className="px-3 py-2 w-28 text-center"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() =>
                                          navigate(
                                            `/variables/${selectedWorkspace}/${repo.slug}`
                                          )
                                        }
                                        className="p-0 h-auto text-xs"
                                      >
                                        <Database className="w-3 h-3 mr-1" />{" "}
                                        Variables
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                // Flat repository list (original view)
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-muted border-b border-border">
                      <th className="px-3 py-2 text-left font-medium w-12">
                        Select
                      </th>
                      <th className="px-3 py-2 text-left font-medium w-full">
                        Repository
                      </th>
                      <th className="px-3 py-2 text-left font-medium w-36">
                        Project
                      </th>
                      <th className="px-3 py-2 text-center font-medium w-24">
                        Type
                      </th>
                      <th className="px-3 py-2 text-center font-medium w-28">
                        Variables
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRepositories.map((repo) => (
                      <tr
                        key={repo.slug}
                        className="border-b border-border hover:bg-muted/30 cursor-pointer"
                        onClick={() => handleRepositoryToggle(repo.slug)}
                      >
                        <td
                          className="px-3 py-2 w-12"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-center">
                            <Checkbox
                              checked={selectedRepositories.includes(repo.slug)}
                              onCheckedChange={() =>
                                handleRepositoryToggle(repo.slug)
                              }
                              id={`repo-${repo.slug}`}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 w-full">
                          <div className="font-medium text-left">
                            {repo.name}
                          </div>
                        </td>
                        <td className="px-3 py-2 w-36">
                          {repo.project ? (
                            <div className="text-xs">{repo.project.name}</div>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              No Project
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 w-24 text-center">
                          <div className="inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs">
                            {repo.is_private ? "Private" : "Public"}
                          </div>
                        </td>
                        <td
                          className="px-3 py-2 w-28 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() =>
                              navigate(
                                `/variables/${selectedWorkspace}/${repo.slug}`
                              )
                            }
                            className="p-0 h-auto text-xs"
                          >
                            <Database className="w-3 h-3 mr-1" /> Variables
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>
      ) : selectedWorkspace ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No repositories found</p>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            Select a workspace to view repositories
          </p>
        </div>
      )}
    </div>
  );
};

export default PipelinesPage;
